import React from 'react';
import PropTypes from 'prop-types';
import {
  Column,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';

const pluralise = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The CSV endpoint continues past per-row failures, so a 200 routinely carries
 * both applied and rejected rows. The tally shows either way — a clean batch
 * still needs confirming — and every rejected row is listed with the server's
 * own reason so the admin can fix the file and re-upload.
 *
 * Results-only: the dialog's actions ("Upload another" / "Done") live in the
 * DialogActions footer so the design system owns their position and spacing.
 */
export const CsvUploadResults = ({ result }) => {
  const {
    updatedCount, removedCount, unchangedCount, failedRows,
  } = result;

  return (
    <Column gap={4}>
      <Column gap={1}>
        <Typography variant="body1">{`${updatedCount} updated`}</Typography>
        <Typography variant="body1">{`${removedCount} removed`}</Typography>
        <Typography variant="body1">{`${unchangedCount} unchanged`}</Typography>
      </Column>

      {failedRows.length > 0 ? (
        <Column gap={2}>
          <Typography variant="body1" className="text-error">
            {`${pluralise(failedRows.length, 'row')} failed`}
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedRows.map((failedRow) => (
                  <TableRow key={`${failedRow.id}-${failedRow.reason}`}>
                    <TableCell>{failedRow.id}</TableCell>
                    <TableCell>{failedRow.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Column>
      ) : null}
    </Column>
  );
};

CsvUploadResults.propTypes = {
  result: PropTypes.shape({
    updatedCount: PropTypes.number.isRequired,
    removedCount: PropTypes.number.isRequired,
    unchangedCount: PropTypes.number.isRequired,
    failedRows: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        reason: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
};
